import { Box } from "@chakra-ui/react";
import React from "react";
import {
  AlertIcon,
  CalendarIcon,
  CornerDownRightIcon,
  GlobeIcon,
  LightningIcon,
  MinusCircleIcon,
  MoneyIcon,
  PercentIcon,
  PersonWithCheckIcon,
  RefreshIcon,
  SearchIcon,
  SettingsIcon,
  ShoppingBagIcon,
  SparklesIcon,
  SunshineIcon,
  TargetIcon,
  TrendIcon,
  TruckIcon,
} from "ui/icons";
import hero from "../../../../public/assets/cdp-industries/heros/automotive.png";
import { dataBreachStat, personalizedExperiencesStat, revenueReferenceCopy, type CdpIndustryConfig } from "./shared";

export const automotiveCdpConfig: CdpIndustryConfig = {
  icon: TruckIcon,
  name: "automotive",
  pluralNoun: "auto companies",
  color: "violet",
  seoCopy:
    "Leverage all your automotive data, including customers, vehicles, transactions, service records, dealerships, and inventory.",
  heroImage: {
    src: hero,
    alt: "A photograph of a sports car with a list of example data tables overlayed. The data tables included are for vehicles, service records, customers, dealerships, and inventory.",
  },
  adoptingCdpsDescription: (
    <>
      Auto customers are more informed than ever before and they have more options than ever before. They expect car
      shopping and service to feel seamless. {revenueReferenceCopy}
    </>
  ),
  featuredOutcomes: [
    {
      icon: TrendIcon,
      color: "blue",
      title: "Grow LTV",
    },
    {
      icon: PersonWithCheckIcon,
      color: "green",
      title: "Increase conversions",
    },
    {
      icon: MoneyIcon,
      color: "yellow",
      title: "Drive incremental revenue",
    },
  ],
  dataStats: [
    {
      title: "$1.5T",
      description: (
        <>
          estimated auto loan <Box as="br" display={{ base: "block", lg: "none" }} />
          debt in the U.S.
        </>
      ),
      href: "https://www.federalreserve.gov/releases/g19/HIST/cc_hist_memo_levels.html",
    },
    personalizedExperiencesStat,
    dataBreachStat,
  ],
  personalizationCards: [
    {
      title: "Cars.com leverages Hightouch to deliver re-engagement emails to increase conversions by 3%",
      href: "/customers/cars",
    },
    {
      title:
        "Auto Trader is using individual session data to personalize lifecycle marketing campaigns, to increase new car buyer engagement by 20%",
      href: "/customers/auto-trader",
    },
    {
      title: "True Car is monetizing 1st party audiences to dealerships across 550+ car buying websites",
      href: "https://www.prnewswire.com/news-releases/truecar-marketing-solutions-helps-dealers-conquest-new-customers-drive-service-revenue-and-more-302051442.html",
    },
  ],
  useCases: {
    performanceMarketing: [
      // First row
      {
        icon: PersonWithCheckIcon,
        title: "Acquisition campaigns",
        description:
          "Drive new vehicle purchases or marketplace sign-ups by using demographic and browsing data to target potential customers and lookalike audiences with tailored promotions.",
      },
      {
        icon: MinusCircleIcon,
        title: "Suppression campaigns",
        description:
          "Reduce wasted ad spend by suppressing recent buyers from acquisition campaigns using CRM and purchase data, reallocating budget toward net-new prospects.",
      },
      {
        icon: TargetIcon,
        title: "Retargeting campaigns",
        description:
          "Re-engage users who browsed vehicles or products but didn’t convert by serving dynamic ads showcasing their viewed items, recovering lost leads.",
      },
      // Second row
      {
        icon: PercentIcon,
        title: "Seasonal promotions",
        description:
          "Drive purchases during key automotive sales events by promoting seasonal discounts like end-of-year clearance or holiday offers, increasing vehicle sales.",
      },
      {
        icon: SettingsIcon,
        title: "Conversion APIs",
        description:
          "Share online and offline conversion data with ad platforms, enabling algorithms to better identify and target users with a higher likelihood of converting to improve campaign performance.",
      },
      {
        icon: GlobeIcon,
        title: "Automotive media networks",
        description:
          "Monetize customer insights by offering anonymized vehicle ownership and service data to brand partners for targeted advertising, generating incremental revenue through audience data sales.",
      },
    ],
    lifecycleMarketing: [
      // First row
      {
        icon: MoneyIcon,
        title: "Upsell & cross-sell campaigns",
        description: "Recommend extended warranties, premium accessories, or service plans to increase customer value.",
      },
      {
        icon: AlertIcon,
        title: "Service reminders",
        description: "Notify customers when maintenance is due to improve retention and service revenue.",
      },
      {
        icon: RefreshIcon,
        title: "Reactivation campaigns",
        description: "Offer service discounts or incentives to bring back lapsed customers.",
      },
      // Second row
      {
        icon: SunshineIcon,
        title: "Seasonal care campaigns",
        description: "Highlight seasonal services like tire installations and AC checks to boost revenue.",
      },
      {
        icon: MoneyIcon,
        title: "Trade-in campaigns",
        description: "Encourage existing owners to upgrade with trade-in incentives.",
      },
      {
        icon: ShoppingBagIcon,
        title: "Accessory campaigns",
        description: "Promote lifestyle accessories like bike racks or floor mats to drive incremental revenue.",
      },
    ],
    onsitePersonalization: [
      // First row
      {
        icon: LightningIcon,
        title: "Dynamic home page content",
        description:
          "Display featured vehicles, trending models, and financing options based on browsing history to increase engagement and lead generation.",
      },
      {
        icon: SearchIcon,
        title: "Dynamic search results",
        description:
          "Adjust search rankings to highlight best deals, trending vehicles, or nearby inventory, improving search-to-purchase conversion rates.",
      },
      {
        icon: CornerDownRightIcon,
        title: "Next best action",
        description:
          "Guide users toward completing high-impact actions like scheduling a test drive, getting pre-approved for financing, or contacting a dealer to boost lead conversion.",
      },
      // Second row
      {
        icon: PersonWithCheckIcon,
        title: "Vehicle upgrade suggestions",
        description:
          "Use ownership duration and mileage data to recommend upgrading to newer models, encouraging repeat purchases and brand loyalty.",
      },
      {
        icon: SparklesIcon,
        title: "Interactive feature demonstrations",
        description:
          "Provide interactive walkthroughs or videos showcasing unique features like autonomous driving, infotainment systems, or safety technology to create excitement and increase engagement.",
      },
      {
        icon: CalendarIcon,
        title: "Test drive scheduling",
        description:
          "Personalize booking prompts based on vehicles users have browsed or saved, streamlining the test drive process and increasing conversion rates.",
      },
    ],
  },
  considerations: {
    dataModeling: {
      traditional: "Uses predefined models that may not fully capture ownership lifecycles or service intervals",
      composable: "Supports tailored models to handle vehicle ownership, service histories, and purchasing preferences",
    },
    audienceManagement: {
      traditional:
        "Supports broad segmentation but struggles with identifying prospective buyers or vehicle upgrade needs",
      composable:
        "Enables granular segmentation based on ownership duration, service records, and customer preferences",
    },
    customerJourneys: {
      traditional: "Provides standard templates for campaigns like test drive scheduling or service reminders",
      composable:
        "Powers fully adaptable journeys for events like lease renewals, service promotions, and first-time buyer campaigns",
    },
    identityResolution: {
      traditional:
        "Relies on out-of-the-box algorithms that may not unify profiles across dealership systems and vehicle finance platforms",
      composable:
        "Supports custom algorithms to unify profiles from dealership records, finance systems, and aftermarket services",
    },
  },
  audienceOverlap: {
    title: "High-intent car buyers",
    firstCategoryLabel: "Test drive no-shows",
    secondCategoryLabel: "Online build & price tool users",
  },
};
